/* ================================================================== */
/*  KuberNodes Platform Types                                         */
/*                                                                    */
/*  The sovereign developer cloud type system.                        */
/*  Every type here represents infrastructure developers depend on.   */
/*  Not features. Infrastructure.                                     */
/* ================================================================== */

/* ================================================================== */
/*  Layer 4: Control & Governance                                     */
/* ================================================================== */

/** Organization — the billing and governance boundary */
export interface Organization {
  id: string;
  name: string;
  slug: string;
  plan: PlanTier;
  ownerId: string;
  members: OrgMember[];
  createdAt: string;
  settings: OrgSettings;
  usage: UsageSummary;
}

export type PlanTier = 'free' | 'builder' | 'team' | 'enterprise';

export interface OrgMember {
  userId: string;
  email: string;
  displayName: string;
  avatarUrl?: string;
  role: OrgRole;
  joinedAt: string;
  lastActive: string;
}

export type OrgRole = 'owner' | 'admin' | 'developer' | 'viewer' | 'member';

export interface OrgSettings {
  defaultChainId: number;
  aiModel: AiModelPreference;
  deploymentApproval: 'auto' | 'manual' | 'review';
  auditLogRetentionDays: number;
  ipAllowlist?: string[];
}

export type AiModelPreference =
  | 'auto'
  | 'gpt-4o'
  | 'gpt-4o-mini'
  | 'gemini-1.5-flash'
  | 'gemini-1.5-pro'
  | 'claude-3.5-sonnet'
  | 'claude-3-opus'
  | 'claude-3-haiku'
  | 'claude-sonnet'
  | 'deepseek-coder';

/* -- User ---------------------------------------------------------- */

export interface PlatformUser {
  id: string;
  email: string;
  displayName: string;
  avatarUrl?: string;
  walletAddresses: string[];
  organizations: OrgRef[];
  activeOrgId: string;
  preferences: UserPreferences;
  createdAt: string;
}

export interface OrgRef {
  id: string;
  name: string;
  slug: string;
  role: OrgRole;
}

export interface UserPreferences {
  theme: 'dark' | 'light';
  editorFontSize: number;
  editorTabSize: number;
  aiAutoSuggest: boolean;
  terminalFontSize: number;
  defaultChainId: number;
}

/* -- Project ------------------------------------------------------- */

export interface Project {
  id: string;
  orgId: string;
  name: string;
  slug: string;
  description?: string;
  framework: ProjectFramework;
  environments: Environment[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  gitRepo?: string;
  tags: string[];
}

export type ProjectFramework = 'hardhat' | 'foundry' | 'truffle' | 'anchor' | 'custom';

/* ================================================================== */
/*  Environments & Secrets (Layer 1 + Layer 4)                        */
/* ================================================================== */

export interface Environment {
  id: string;
  projectId: string;
  name: string;
  slug: 'development' | 'staging' | 'production' | string;
  chainId: number;
  rpcUrl: string;
  blockExplorerUrl?: string;
  secrets: SecretRef[];
  variables: EnvVariable[];
  isDefault: boolean;
  createdAt: string;
}

export interface SecretRef {
  /** Key name — value is never sent to the client */
  key: string;
  /** When the secret was last updated */
  updatedAt: string;
  /** Who last updated it */
  updatedBy: string;
  /** Masked preview, e.g. "sk-...x4f2" */
  maskedValue: string;
}

export interface EnvVariable {
  key: string;
  value: string;
  isSecret: boolean;
  description?: string;
}

/* ================================================================== */
/*  Layer 3: Web3 Infrastructure                                      */
/* ================================================================== */

export interface ChainConfig {
  chainId: number;
  name: string;
  shortName: string;
  logo: string;
  color: string;
  type: 'mainnet' | 'testnet' | 'local';
  nativeCurrency: { name: string; symbol: string; decimals: number };
  rpcUrl: string;
  wsUrl?: string;
  blockExplorerUrl?: string;
  faucetUrl?: string;
  /** Whether this chain is managed by KuberNodes RPC gateway */
  isManaged: boolean;
  /** Average block time in seconds */
  blockTimeSeconds: number;
}

export interface RpcGatewayStats {
  chainId: number;
  totalRequests: number;
  requestsToday: number;
  avgLatencyMs: number;
  p99LatencyMs: number;
  errorRate: number;
  status: 'healthy' | 'degraded' | 'down';
  lastChecked: string;
}

export interface GasEstimate {
  chainId: number;
  baseFeeGwei: number;
  priorityFeeGwei: number;
  estimatedCostUsd: number;
  speed: 'slow' | 'standard' | 'fast';
  timestamp: string;
}

export interface ContractVerification {
  contractAddress: string;
  chainId: number;
  status: 'pending' | 'verified' | 'failed';
  explorerUrl?: string;
  verifiedAt?: string;
  error?: string;
}

/* ================================================================== */
/*  Layer 2: Intelligence                                             */
/* ================================================================== */

export interface AiModel {
  id: string;
  name: string;
  provider: 'azure' | 'openai' | 'anthropic' | 'google' | 'deepseek' | 'local';
  capabilities: AiCapability[];
  contextWindow: number;
  costPer1kTokens: number;
  isDefault: boolean;
  status: 'available' | 'degraded' | 'offline';
}

export type AiCapability =
  | 'code-generation'
  | 'code-review'
  | 'security-audit'
  | 'gas-optimization'
  | 'test-generation'
  | 'code-explanation'
  | 'architecture'
  | 'debugging';

export interface AiUsage {
  tokensUsed: number;
  tokensLimit: number;
  requestsToday: number;
  costToday: number;
  modelBreakdown: Record<string, number>;
}

/* ================================================================== */
/*  Usage, Billing & Audit (Layer 4)                                  */
/* ================================================================== */

export interface UsageSummary {
  /** Compute hours used this billing period */
  computeHours: number;
  computeLimit: number;
  /** Storage in GB */
  storageGb: number;
  storageLimit: number;
  /** RPC requests this period */
  rpcRequests: number;
  rpcLimit: number;
  /** AI tokens this period */
  aiTokens: number;
  aiTokenLimit: number;
  /** Number of deployments */
  deployments: number;
  deploymentsLimit: number;
  /** Billing period */
  periodStart: string;
  periodEnd: string;
}

export interface AuditLogEntry {
  id: string;
  orgId: string;
  userId: string;
  userEmail: string;
  action: AuditAction;
  resource: string;
  resourceId: string;
  metadata?: Record<string, unknown>;
  ipAddress: string;
  timestamp: string;
}

export type AuditAction =
  | 'workspace.create'
  | 'workspace.delete'
  | 'workspace.start'
  | 'workspace.stop'
  | 'project.create'
  | 'project.delete'
  | 'deploy.mainnet'
  | 'deploy.testnet'
  | 'secret.create'
  | 'secret.update'
  | 'secret.delete'
  | 'member.invite'
  | 'member.remove'
  | 'member.role_change'
  | 'settings.update'
  | 'billing.plan_change'
  | 'auth.login'
  | 'auth.logout';

/* ================================================================== */
/*  Logs & Monitoring (Layer 1 + Layer 5)                             */
/* ================================================================== */

export interface LogEntry {
  id: string;
  workspaceId: string;
  source: LogSource;
  level: LogLevel;
  message: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

export type LogSource = 'workspace' | 'build' | 'deploy' | 'runtime' | 'rpc' | 'ai' | 'system';
export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'fatal';

export interface ResourceMetrics {
  workspaceId: string;
  cpuPercent: number;
  memoryMb: number;
  memoryLimitMb: number;
  diskMb: number;
  diskLimitMb: number;
  networkInKb: number;
  networkOutKb: number;
  timestamp: string;
}

/* ================================================================== */
/*  Plan Limits                                                       */
/* ================================================================== */

export interface PlanLimits {
  tier: PlanTier;
  maxWorkspaces: number;
  maxProjects: number;
  maxMembers: number;
  computeHoursPerMonth: number;
  storageGb: number;
  rpcRequestsPerMonth: number;
  aiTokensPerMonth: number;
  deploymentsPerMonth: number;
  features: PlanFeature[];
}

export type PlanFeature =
  | 'custom-domains'
  | 'priority-support'
  | 'sso'
  | 'audit-logs'
  | 'ip-allowlist'
  | 'mainnet-deploy'
  | 'team-collaboration'
  | 'advanced-ai'
  | 'dedicated-rpc';

export const PLAN_LIMITS: Record<PlanTier, PlanLimits> = {
  free: {
    tier: 'free',
    maxWorkspaces: 2,
    maxProjects: 3,
    maxMembers: 1,
    computeHoursPerMonth: 50,
    storageGb: 1,
    rpcRequestsPerMonth: 10_000,
    aiTokensPerMonth: 50_000,
    deploymentsPerMonth: 20,
    features: [],
  },
  builder: {
    tier: 'builder',
    maxWorkspaces: 5,
    maxProjects: 10,
    maxMembers: 1,
    computeHoursPerMonth: 200,
    storageGb: 5,
    rpcRequestsPerMonth: 100_000,
    aiTokensPerMonth: 500_000,
    deploymentsPerMonth: 100,
    features: ['mainnet-deploy', 'advanced-ai'],
  },
  team: {
    tier: 'team',
    maxWorkspaces: 20,
    maxProjects: 50,
    maxMembers: 10,
    computeHoursPerMonth: 1000,
    storageGb: 25,
    rpcRequestsPerMonth: 1_000_000,
    aiTokensPerMonth: 2_000_000,
    deploymentsPerMonth: 500,
    features: [
      'mainnet-deploy',
      'advanced-ai',
      'team-collaboration',
      'audit-logs',
      'custom-domains',
    ],
  },
  enterprise: {
    tier: 'enterprise',
    maxWorkspaces: -1, // unlimited
    maxProjects: -1,
    maxMembers: -1,
    computeHoursPerMonth: -1,
    storageGb: -1,
    rpcRequestsPerMonth: -1,
    aiTokensPerMonth: -1,
    deploymentsPerMonth: -1,
    features: [
      'mainnet-deploy',
      'advanced-ai',
      'team-collaboration',
      'audit-logs',
      'custom-domains',
      'sso',
      'ip-allowlist',
      'priority-support',
      'dedicated-rpc',
    ],
  },
};
